import React, { useEffect, useMemo } from "react";
import { FileIcon, FileTree } from "@promptlayer/ui";
import { BoxesIcon, FileTextIcon } from "lucide-react";

import { PROVIDER_CONFIGS } from "../provider-config";
import type { EditorTreeNode } from "../types";
import { useSkillCollectionEditorStore } from "../store";
import {
  AVAILABLE_PROVIDER_SELECTIONS,
  PROVIDER_SELECTION_LABELS,
  getProviderSelectionLogoProvider,
  PRESET_PROTECTED_PATHS,
  providerSelectionToPreset,
  VENDOR_AGNOSTIC_PROVIDER,
  type SkillCollectionProviderSelection,
} from "../skill-presets";

import { useExpandedFolders } from "./hooks/useExpandedFolders";
import { usePendingTreeAction } from "./hooks/usePendingTreeAction";
import { useFolderImport } from "./hooks/useFolderImport";
import { CreateSkillCollectionFileDialog } from "./CreateSkillCollectionFileDialog";

import { ProviderIcon, ProviderWithLogo } from "@/components/ProviderWithLogo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProgressBar, ProgressBarSize } from "@/components/ui/ProgressBar";

interface EditorSidebarProps {
  tree: EditorTreeNode[];
  dirtyFilePaths: Set<string>;
  selectedPath: string | null;
  onSelectFile: (path: string | null) => void;
  onMoveNode: (
    sourcePath: string,
    sourceType: "file" | "folder",
    targetFolderPath: string | null,
  ) => void;
  onDeleteNode: (path: string, type: "file" | "folder") => void;
  onCreateFile: (path: string, options?: { initialContent?: string }) => void;
  onCreateFolder: (path: string) => void;
  getCreateError: (
    parentPath: string | null,
    itemType: "file" | "folder",
    value: string,
  ) => string | null;
  getRenameError: (path: string, itemType: "file" | "folder", value: string) => string | null;
  onRenameNode: (path: string, type: "file" | "folder", value: string) => void;
  provider: SkillCollectionProviderSelection;
  onProviderChange?: (preset: SkillCollectionProviderSelection) => void;
}

export const EditorSidebar = ({
  tree,
  dirtyFilePaths,
  selectedPath,
  onSelectFile,
  onMoveNode,
  onDeleteNode,
  onCreateFile,
  onCreateFolder,
  getCreateError,
  getRenameError,
  onRenameNode,
  provider,
  onProviderChange,
}: EditorSidebarProps) => {
  const store = useSkillCollectionEditorStore();
  const presetProvider = providerSelectionToPreset(provider);
  const isVendorAgnostic = provider === VENDOR_AGNOSTIC_PROVIDER;
  const renderFileIcon = useMemo(() => {
    if (presetProvider !== "openclaw") {
      return undefined;
    }

    return (path: string, className?: string) => {
      if (path === "AGENTS.md") {
        return <ProviderIcon provider="openclaw" className={className ?? "h-4 w-4 shrink-0"} />;
      }

      return <FileIcon path={path} className={className} />;
    };
  }, [presetProvider]);

  const isNodeProtected = useMemo(() => {
    const protectedPaths = presetProvider ? PRESET_PROTECTED_PATHS[presetProvider] : undefined;
    const skillRoot = presetProvider ? PROVIDER_CONFIGS[presetProvider].skillRoot : null;
    const skillRootPrefix = skillRoot ? `${skillRoot}/` : null;
    if (!protectedPaths || protectedPaths.length === 0) {
      return undefined;
    }
    return (path: string): boolean =>
      protectedPaths.some(protectedPath => {
        if (!protectedPath.endsWith("/")) {
          return path === protectedPath;
        }

        const folderPath = protectedPath.slice(0, -1);
        const isProtectedBranch = path === folderPath || path.startsWith(protectedPath);
        if (!isProtectedBranch) {
          return false;
        }

        if (skillRoot && (path === skillRoot || path.startsWith(skillRootPrefix ?? ""))) {
          return false;
        }

        return true;
      });
  }, [presetProvider]);

  const { expandedFolders, setExpandedFolders, toggleFolder } = useExpandedFolders(selectedPath);

  const {
    pendingAction,
    activeDirectoryPath,
    setActiveDirectoryPath,
    createFileParentPath,
    isCreateFileDialogOpen,
    handleCreateFileDialogClose,
    handlePendingActionValueChange,
    handleCancelPendingAction,
    handleSubmitPendingAction,
    handleRequestCreateFile,
    handleRequestCreateFolder,
    handleRequestRename,
  } = usePendingTreeAction({
    getCreateError,
    getRenameError,
    onCreateFile,
    onCreateFolder,
    onRenameNode,
    setExpandedFolders,
  });

  const { importProgress, isImportingFolder, handleFolderImport } = useFolderImport({
    store,
    provider,
    onProviderChange,
    setExpandedFolders,
  });

  useEffect(() => {
    if (selectedPath) {
      setActiveDirectoryPath(null);
    }
  }, [selectedPath, setActiveDirectoryPath]);

  return (
    <>
      <div className="flex w-[276px] shrink-0 flex-col border-r border-gray-200 bg-white">
        {importProgress && importProgress.total > 0 && (
          <div className="border-b border-gray-200 px-3 py-2">
            <div className="mb-1 text-xs text-gray-500">
              Importing {importProgress.current} / {importProgress.total} files...
            </div>
            <ProgressBar
              isLoading={importProgress.current < importProgress.total}
              progress={Math.round((importProgress.current / importProgress.total) * 100)}
              showProgress
              fullWidth
              size={ProgressBarSize.LARGE}
            />
          </div>
        )}
        <FileTree
          tree={tree}
          modifiedFilePaths={dirtyFilePaths}
          selectedPath={selectedPath}
          activeDirectoryPath={activeDirectoryPath}
          onSelectFile={onSelectFile}
          expandedFolders={expandedFolders}
          onToggleFolder={toggleFolder}
          onSelectDirectory={setActiveDirectoryPath}
          onMoveNode={onMoveNode}
          onDeleteNode={onDeleteNode}
          onCreateFile={handleRequestCreateFile}
          onCreateFolder={handleRequestCreateFolder}
          onRequestRename={handleRequestRename}
          pendingAction={pendingAction}
          onPendingActionValueChange={handlePendingActionValueChange}
          onSubmitPendingAction={handleSubmitPendingAction}
          onCancelPendingAction={handleCancelPendingAction}
          onFolderImport={handleFolderImport}
          folderImportDisabled={isImportingFolder}
          isNodeProtected={isNodeProtected}
          renderFileIcon={renderFileIcon}
          emptyMessage="No files yet"
          emptyStateContent={
            <div className="flex flex-col items-center px-4 py-6 text-center">
              <div className="mb-3 rounded-full bg-gray-100 p-2.5 text-gray-400">
                <FileTextIcon className="h-4 w-4" />
              </div>
              <p className="text-sm font-medium text-gray-800">No files yet</p>
              <p className="mt-1 max-w-[220px] text-xs leading-5 text-gray-500">
                {isVendorAgnostic
                  ? "Create or import files to begin building a portable skill collection."
                  : "Create or import files to begin building this collection."}
              </p>
            </div>
          }
        />
        {onProviderChange && (
          <div className="border-t border-gray-200 px-3 py-2">
            <Select value={provider} onValueChange={onProviderChange}>
              <SelectTrigger className="h-8 w-full text-xs">
                <SelectValue placeholder="Select layout..." />
              </SelectTrigger>
              <SelectContent>
                {AVAILABLE_PROVIDER_SELECTIONS.map(option => (
                  <SelectItem key={option} value={option}>
                    <div className="flex items-center gap-2">
                      {option === VENDOR_AGNOSTIC_PROVIDER ? (
                        <BoxesIcon className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ProviderWithLogo
                          provider={getProviderSelectionLogoProvider(option) ?? ""}
                          className="!mr-0"
                          hideName
                        />
                      )}
                      <span>{PROVIDER_SELECTION_LABELS[option]}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <CreateSkillCollectionFileDialog
        open={isCreateFileDialogOpen}
        parentPath={createFileParentPath}
        provider={provider}
        onOpenChange={handleCreateFileDialogClose}
        onCreateFile={onCreateFile}
        getCreateError={getCreateError}
      />
    </>
  );
};
